import { useEffect } from "react";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Tooltip,
  Divider,
  DatePicker,
  InputNumber,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import dayjs from "dayjs";

export default function InstrumentStructureForm({
  onFinish,
  initialValues,
  form,
  currency,
  dayCountConventionOptions,
  couponFrequencyOptions,
  businessDayConventionOptions,
  instrumentTypeOptions,
  instrumentTypeId,
  holiday,
  onCurrencyChange, // ← new prop
}) {
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      form.setFieldsValue({
        ...initialValues,
        issueDate: initialValues.issueDate
          ? dayjs(initialValues.issueDate)
          : null,
        maturityDate: initialValues.maturityDate
          ? dayjs(initialValues.maturityDate)
          : null,
      });
    }
  }, [initialValues, form]);

  const instrumentTypeCode = instrumentTypeOptions?.find(
    (t) => t.value === instrumentTypeId,
  )?.code;

  const isFixedBond = instrumentTypeCode === "FIXED_RATE_BOND";
  const isFRN = instrumentTypeCode === "FLOAT_RATE_NOTE";
  const isCouponInstrument = isFixedBond || isFRN;

  useEffect(() => {
    if (!isCouponInstrument) {
      form.setFieldsValue({
        couponRate: null,
        couponFrequencyId: null,
        couponBasis: null,
        resetIndex: null,
      });
    }

    if (!isFRN) {
      form.setFieldsValue({
        resetIndex: null,
      });
    }
  }, [instrumentTypeCode]);

  return (
    <div>
      <h3 style={{ marginBottom: "8px", fontWeight: "600" }}>
        Instrument Structure
      </h3>
      <p style={{ marginBottom: "24px", color: "#667085", fontSize: "14px" }}>
        These fields define the instrument's economic behaviour.
      </p>

      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={initialValues}
      >
        {/* For All Instruments Section */}
        <h4
          style={{ marginBottom: "16px", fontWeight: "600", marginTop: "16px" }}
        >
          For All Instruments
        </h4>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Currency
                  <Tooltip title="Currency in which the security is denominated and settled.">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="currencyCode"
              rules={[{ required: true, message: "Please enter currency" }]}
            >
              <Select
                showSearch
                placeholder="NGN, USD, ZAR, GBP"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children
                    ?.toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val) => {
                  // Notify parent so it can filter productCodes in real time
                  onCurrencyChange?.(val);
                }}
              >
                {currency.map((c) => (
                  <Select.Option key={c.value} value={c.value}>
                    {c.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Face Value / Denomination
                  <Tooltip title="Nominal or par value of the security unit.">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="faceValueDenomination"
              rules={[
                { required: true, message: "Please enter face value" },
                {
                  type: "number",
                  min: 1,
                  message: "Face value must be a positive number",
                },
              ]}
            >
              <InputNumber
                className="bond-input"
                style={{ width: "100%", height: 44 }}
                placeholder="Commonly 1,000 or 100,000"
                min={1}
                formatter={(value) =>
                  value
                    ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    : ""
                }
                parser={(value) => value.replace(/,/g, "")}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          {/* Issue Date */}
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Issue Date
                  <Tooltip title="Date on which the security was issued and became effective.">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="issueDate"
              rules={[{ required: true, message: "Please select issue date" }]}
            >
              <DatePicker
                style={{ width: "100%", height: 44 }}
                placeholder="Start of instrument existence"
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                onChange={() => form.validateFields(["maturityDate"])}
              />
            </Form.Item>
          </Col>

          {/* Maturity Date */}
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Maturity Date
                  <Tooltip title="Date on which the security expires and principal is repaid.">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="maturityDate"
              rules={[
                { required: true, message: "Please select maturity date" },
                {
                  validator: (_, value) => {
                    const issueDate = form.getFieldValue("issueDate");
                    if (!value || !issueDate) return Promise.resolve();
                    if (!dayjs(value).isAfter(dayjs(issueDate))) {
                      return Promise.reject(
                        new Error("Maturity date must be after issue date"),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%", height: 44 }}
                placeholder="End of instrument lifespan"
                disabledDate={(current) => {
                  const issueDate = form.getFieldValue("issueDate");
                  if (!issueDate) return false;
                  return current && current <= dayjs(issueDate).endOf("day");
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Day Count Convention
                  <Tooltip title="Convention used to calculate interest accruals.">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="dayCountConventionId"
              rules={[
                {
                  required: true,
                  message: "Please select day count convention",
                },
              ]}
            >
              <Select
                placeholder="ACT/365, ACT/360, 30/360"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
              >
                {dayCountConventionOptions.map((dayCount) => (
                  <Select.Option key={dayCount.value} value={dayCount.value}>
                    {dayCount.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Business Day Convention
                  <Tooltip title="Rule that defines how dates are adjusted if they fall on non-business days.">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="businessDayConventionId"
              rules={[
                {
                  required: true,
                  message: "Please select business day convention",
                },
              ]}
            >
              <Select
                placeholder="Following, Modified Following"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
              >
                {businessDayConventionOptions.map((business) => (
                  <Select.Option key={business.value} value={business.value}>
                    {business.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Holiday Calendar
                  <Tooltip title="Calendar defining non-working days applicable to the security. FinSys Calendar">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="holidayCalendar"
              rules={[
                { required: true, message: "Please select holiday calendar" },
              ]}
            >
              <Select
                placeholder="NG, ZA, US"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
              >
                {holiday.map((h) => (
                  <Select.Option key={h.value} value={h.value}>
                    {h.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {isCouponInstrument && (
          <>
            <Divider style={{ margin: "32px 0" }} />

            <h4 style={{ marginBottom: "16px", fontWeight: "600" }}>
              For Coupon Instruments (Bonds / FRNs)
            </h4>

            <Row>
              <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item label="Coupon Rate" name="couponRate">
                  <InputNumber
                    placeholder="e.g., 12.50%"
                    style={{ width: "100%", height: 44 }}
                    formatter={(value) =>
                      value
                        ? value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        : ""
                    }
                    parser={(value) => value.replace(/,/g, "")}
                    min={1}
                  />
                </Form.Item>
              </Col>

              <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item label="Coupon Frequency" name="couponFrequencyId">
                  <Select
                    style={{ height: 44 }}
                    suffixIcon={<ChevronDown color="#667085" width={20} />}
                  >
                    {couponFrequencyOptions.map((c) => (
                      <Select.Option key={c.value} value={c.value}>
                        {c.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={12} style={{ padding: "0px 8px" }}>
                <Form.Item label="Coupon Basis" name="couponBasis">
                  <Select
                    style={{ height: 44 }}
                    suffixIcon={<ChevronDown color="#667085" width={20} />}
                  >
                    <Select.Option value="Fixed">Fixed</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              {isFRN && (
                <Col span={12} style={{ padding: "0px 8px" }}>
                  <Form.Item label="Reset Index (FRN)" name="resetIndex">
                    <Select
                      style={{ height: 44 }}
                      suffixIcon={<ChevronDown color="#667085" width={20} />}
                    >
                      <Select.Option value="SOFR">SOFR</Select.Option>
                      <Select.Option value="NIBOR">NIBOR</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              )}
            </Row>
          </>
        )}
      </Form>
    </div>
  );
}
